import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/helpers";

const ROLES = [
  { v: "admin", label: "Administrador", hint: "Gestiona usuarios + todo" },
  { v: "editor", label: "Editor", hint: "CRUD de datos, sin gestión de usuarios" },
];

const blank = { email: "", name: "", role: "editor", password: "" };

export default function Users() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(false);
  const [resetFor, setResetFor] = useState(null); // user being password-reset by admin
  const [form, setForm] = useState(blank);
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.http.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando usuarios");
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...blank }); setModal(true); };
  const openEdit = (u) => { setForm({ id: u.id, email: u.email, name: u.name || "", role: u.role, password: "" }); setModal(true); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.id) {
        await api.http.put(`/users/${form.id}`, { email: form.email, name: form.name, role: form.role });
        toast.success("Usuario actualizado");
      } else {
        if (!form.password || form.password.length < 8) {
          toast.error("La contraseña debe tener al menos 8 caracteres");
          return;
        }
        await api.http.post(`/users`, form);
        toast.success("Usuario creado");
      }
      await load();
      setModal(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (u.id === current.id) { toast.error("No puedes eliminar tu propio usuario"); return; }
    if (!window.confirm(`¿Eliminar usuario ${u.email}?`)) return;
    try {
      await api.http.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    try {
      await api.http.post(`/users/${resetFor.id}/reset-password`, { password: newPw });
      toast.success("Contraseña actualizada");
      setResetFor(null); setNewPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const roleLabel = (r) =>
    ({ admin: "Administrador", editor: "Editor" }[r] || r);

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1"/>
        <button className="btn btn-primary" onClick={openNew} data-testid="btn-new-user">+ Nuevo usuario</button>
      </div>

      <div className="card overflow-hidden">
        <table className="data">
          <thead><tr>
            <th>Email</th><th>Nombre</th><th>Rol</th><th>Creado</th><th/>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-semibold">{u.email}{u.id === current.id && <span className="badge badge-blue ml-2">Tú</span>}</td>
                <td>{u.name || "—"}</td>
                <td>
                  <span className={`badge ${u.role === "admin" ? "badge-orange" : "badge-blue"}`}>
                    {roleLabel(u.role)}
                  </span>
                </td>
                <td className="text-xs text-slate-500">{formatDate(u.created_at)}</td>
                <td className="text-right whitespace-nowrap">
                  <button className="btn btn-ghost !py-1 !px-2" onClick={() => openEdit(u)}>Editar</button>
                  <button className="btn btn-blue !py-1 !px-2" onClick={() => { setResetFor(u); setNewPw(""); }}>Contraseña</button>
                  <button className="btn btn-danger !py-1 !px-2" disabled={u.id === current.id}
                          onClick={() => remove(u)}>✕</button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan="5" className="text-center text-slate-400 py-6">Cargando...</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit modal */}
      {modal && (
        <div className="modal-backdrop">
          <div className="modal p-6" style={{ maxWidth: 560 }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">{form.id ? "Editar" : "Nuevo"} usuario</h3>
              <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-700 text-2xl">✕</button>
            </div>
            <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className="label">Email *</label>
                <input className="input" type="email" required value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="user-email"/></div>
              <div className="md:col-span-2"><label className="label">Nombre</label>
                <input className="input" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="user-name"/></div>
              <div className="md:col-span-2"><label className="label">Rol *</label>
                <div className="space-y-2">
                  {ROLES.map((r) => (
                    <label key={r.v} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${form.role === r.v ? "bg-orange-50 border-orange-300" : "border-slate-200"}`}>
                      <input type="radio" name="role" className="mt-1" checked={form.role === r.v}
                             onChange={() => setForm({ ...form, role: r.v })}
                             disabled={form.id === current.id}
                             data-testid={`role-${r.v}`}/>
                      <div>
                        <div className="font-semibold text-sm">{r.label}</div>
                        <div className="text-xs text-slate-500">{r.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {form.id === current.id && (
                  <div className="text-[11px] text-slate-500 mt-1">No puedes cambiar tu propio rol.</div>
                )}
              </div>
              {!form.id && (
                <div className="md:col-span-2">
                  <label className="label">Contraseña inicial * (mínimo 8 caracteres)</label>
                  <input className="input" type="password" required minLength={8} value={form.password}
                         onChange={(e) => setForm({ ...form, password: e.target.value })}
                         data-testid="user-password"/>
                </div>
              )}
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={busy} data-testid="user-save">
                  {busy ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetFor && (
        <div className="modal-backdrop">
          <div className="modal p-6" style={{ maxWidth: 420 }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Cambiar contraseña</h3>
              <button onClick={() => setResetFor(null)} className="text-slate-400 hover:text-slate-700 text-2xl">✕</button>
            </div>
            <p className="text-sm text-slate-600 mb-4">Usuario: <span className="font-semibold">{resetFor.email}</span></p>
            <form onSubmit={submitReset} className="space-y-4">
              <div>
                <label className="label">Nueva contraseña (mín. 8)</label>
                <input className="input" type="password" required minLength={8}
                       value={newPw} onChange={(e) => setNewPw(e.target.value)}
                       data-testid="user-new-password"/>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn btn-ghost" onClick={() => setResetFor(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" data-testid="user-reset-save">Actualizar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
